<script>
import variables from '@/styles/_varsJs.scss'
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'

export default {
  components: { AscRequiredLabel },

  props: {
    autofocus: {
      type: Boolean,
    },
    counter: {
      type: [Boolean, Number, String],
      default: undefined,
    },
    counterExceedMessage: {
      type: String,
      default: 'Input exceeds character limit.',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    maxLength: {
      type: Number,
      default: undefined,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: undefined,
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    requiredVisible: {
      type: Boolean,
      default: true,
    },
    rules: {
      type: [Object, Array],
      default: undefined,
    },
    textPrefix: {
      type: String,
      default: '',
    },
    updateOnBlur: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    dense: {
      type: Boolean,
      default: false,
    },
    validateOnBlur: {
      type: Boolean,
      default: false,
    },
    noSpacing: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
    prefix: {
      type: String,
      default: undefined,
    },
    suffix: {
      type: String,
      default: undefined,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    labelIcon: {
      type: String,
      default: undefined,
    },
    appendIcon: {
      type: String,
      default: undefined,
    },
    hideSpinButtons: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      focus: false,
      innerValue: this.value,
    }
  },

  computed: {
    model: {
      get() {
        return this.innerValue
      },
      set(value) {
        this.innerValue = value
        if (!this.updateOnBlur) this.emitChanges()
      },
    },
    getThemeColor() {
      return this.readonly ? variables.gray700 : variables.accent600
    },
    hasCounter() {
      return (
        (this.counter !== false && this.counter != null) ||
        (this.counter === true && this.maxLength)
      )
    },
    getMaxValue() {
      return this.counter === true ? this.maxLength : this.counter
    },
    getMaxLength() {
      // If counter is a number then dont consider maxLength
      let x
      const isInt = isNaN(this.counter)
        ? !1
        : ((x = parseFloat(this.counter)), (0 | x) === x)
      return isInt ? undefined : this.maxLength
    },
    getCounterValue() {
      if (!this.hasCounter) return null
      return `${this.counterValue}/${this.getMaxValue}`
    },
    counterValue() {
      return [...(this.innerValue || '').toString()].length
    },
    getRules() {
      const newRules = []
      if (this.hasCounter)
        newRules.push(v => {
          return v === null || v === undefined
            ? true
            : v?.length <= this.getMaxValue || this.counterExceedMessage
        })

      if (this.rules) newRules.push(...this.rules)
      return newRules
    },
    getComponentName() {
      return this.$options.name
    },
    getSharedAttrs() {
      return {
        class: [
          'asc-input-field',
          this.getComponentName,
          this.noSpacing ? 'pt-0 mt-0' : null,
          this.required ? 'is__required' : null,
          this.hasCounter ? `${this.getComponentName}--hasCounter` : null,
          this.hasCounter ? `v-input--has-counter` : null,
        ],
        color: this.getThemeColor,
        counter: this.hasCounter,
        disabled: this.disabled,
        error: this.error,
        'hide-details': this.hideDetails,
        hint: this.hint,
        id: this.id,
        label: this.label,
        maxlength: this.getMaxLength,
        outlined: this.outlined,
        'persistent-hint': this.persistentHint,
        placeholder: this.placeholder,
        readonly: this.readonly,
        required: this.required,
        rules: this.getRules,
        autofocus: this.autofocus,
        autocomplete: 'off',
        dense: this.dense,
        'validate-on-blur': this.validateOnBlur,
        'error-messages': this.errorMessages,
        prefix: this.prefix,
        suffix: this.suffix,
        clearable: this.clearable,
        'data-value': this.value,
        type: this.type,
        'hide-spin-buttons': this.hideSpinButtons,
      }
    },
  },

  watch: {
    focus() {
      if (this.updateOnBlur && !this.focus) {
        this.emitChanges()
      }
    },
    value(val) {
      this.innerValue = val
    },
  },

  methods: {
    emitChanges() {
      this.$emit('update:value', this.innerValue)
      this.$emit('input', this.innerValue)
      this.$emit('change', this.innerValue)
    },
    handleBlur() {
      this.focus = false
      this.$emit('blur', this.innerValue)
    },

    handleClick() {
      this.$emit('click')
    },
  },
}
</script>

<style lang="scss">
.asc-input-field--container {
  .v-input {
    font-size: 14px;
    line-height: 18px;

    &__append-inner {
      .v-icon {
        font-size: 16px;
        color: $gray-500;
      }
    }

    &:not(.v-input--is-disabled) {
      .v-input__append-inner {
        .v-icon {
          color: $gray-700;
        }
      }
    }

    &__slot {
      &::before {
        border-width: 1px 0 0 0;
        border-color: $gray-400 !important;
      }

      &:hover::before {
        border-width: 1px 0 0 0;
        border-color: $gray-800 !important;
      }

      &::after {
        border-width: 1px 0 0 0 !important;
      }

      .v-label {
        color: $gray-700 !important;
        line-height: 18px;
        font-size: 12px;
        height: 18px;
        left: 0 !important;
      }

      .withIcon {
        display: flex;
      }

      .v-label,
      .v-label--active {
        transform: translateY(-18px) scale(1);
      }

      .v-input--selection-controls__input {
        margin-right: 0;
      }

      textarea {
        padding-top: 4px;
      }
    }

    .v-text-field__details {
      .v-messages {
        color: $gray-700;
      }
    }

    &--has-counter {
      .v-text-field__details {
        flex-direction: column-reverse;

        .v-messages {
          min-height: 0;
        }

        .v-counter {
          width: 100%;
          margin-left: 0;
          margin-bottom: 3px;
        }
      }
    }

    &--is-readonly {
      .v-input__slot {
        &::before {
          border: none !important;
        }

        &::after {
          transform: scaleX(0) !important;
        }

        .v-label {
          color: $gray-700 !important;
        }
      }
    }

    &--is-disabled {
      input,
      textarea {
        cursor: not-allowed;
        pointer-events: all !important;
        color: $gray-700;
      }

      .v-input__slot::before {
        border-color: $gray-400 !important;
        border-image: none !important;
      }
    }

    &:not(.v-input--is-disabled) {
      input {
        color: $primary-black;
      }
    }
  }
}
</style>
